import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useTheme } from "../../../context/ThemeContext";

interface Product {
  id: string;
  name: string;
  sku: string;
  price: number;
  cost: number;
  stock: number;
  category: string;
  status: "active" | "inactive" | "out_of_stock";
  visibility: {
    pos: boolean;
    online: boolean;
  };
}

const MOCK_PRODUCTS: Product[] = [
  { id: "1", name: "Premium Headphones", sku: "ELEC-001", price: 149.99, cost: 75.00, stock: 45, category: "Electronics", status: "active", visibility: { pos: true, online: true } },
  { id: "2", name: "Wireless Mouse", sku: "ELEC-002", price: 39.99, cost: 15.00, stock: 120, category: "Electronics", status: "active", visibility: { pos: true, online: true } },
  { id: "3", name: "USB-C Cable", sku: "ELEC-003", price: 12.99, cost: 3.50, stock: 0, category: "Electronics", status: "out_of_stock", visibility: { pos: true, online: false } },
  { id: "4", name: "Cotton T-Shirt", sku: "CLTH-001", price: 24.99, cost: 8.00, stock: 85, category: "Clothing", status: "active", visibility: { pos: true, online: true } },
  { id: "5", name: "Denim Jeans", sku: "CLTH-002", price: 59.99, cost: 22.00, stock: 32, category: "Clothing", status: "active", visibility: { pos: true, online: true } },
  { id: "6", name: "Running Shoes", sku: "CLTH-003", price: 89.99, cost: 35.00, stock: 18, category: "Clothing", status: "inactive", visibility: { pos: false, online: false } },
];

export default function ProductListSection() {
  const { t } = useTranslation("common");
  const { isDarkMode } = useTheme();
  const [searchTerm, setSearchTerm] = useState("");
  const [viewMode, setViewMode] = useState<"table" | "grid">("table");
  const [statusFilter, setStatusFilter] = useState<string>("all");

  const themeClasses = {
    bg: isDarkMode ? "bg-dark-surface" : "bg-white",
    border: isDarkMode ? "border-dark-border" : "border-gray-200",
    text: isDarkMode ? "text-white" : "text-gray-900",
    textSecondary: isDarkMode ? "text-gray-400" : "text-gray-600",
    cardBg: isDarkMode ? "bg-dark-card" : "bg-gray-50",
    inputBg: isDarkMode ? "bg-dark-surface" : "bg-white",
    tableRowHover: isDarkMode ? "hover:bg-dark-card" : "hover:bg-gray-50",
  };

  const filteredProducts = MOCK_PRODUCTS.filter((product) => {
    const matchesSearch = product.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      product.sku.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === "all" || product.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const getStatusBadge = (status: Product["status"]) => {
    const styles = {
      active: "bg-emerald-500/20 text-emerald-500",
      inactive: "bg-gray-500/20 text-gray-500",
      out_of_stock: "bg-red-500/20 text-red-500",
    };
    const labels = {
      active: t("posProducts.statusActive"),
      inactive: t("posProducts.statusInactive"),
      out_of_stock: t("posProducts.statusOutOfStock"),
    };
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${styles[status]}`}>
        {labels[status]}
      </span>
    );
  };

  return (
    <div className="p-4 sm:p-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
        <div>
          <h1 className={`text-2xl font-bold ${themeClasses.text}`}>{t("posProducts.title")}</h1>
          <p className={themeClasses.textSecondary}>{t("posProducts.subtitle")}</p>
        </div>
        <button className="px-4 py-2 rounded-xl bg-primary-500 text-gray-900 font-medium flex items-center gap-2">
          <i className="ri-add-line"></i>
          {t("posProducts.addProduct")}
        </button>
      </div>

      <div className={`${themeClasses.bg} rounded-2xl border ${themeClasses.border} overflow-hidden`}>
        <div className={`p-4 border-b ${themeClasses.border} flex flex-col sm:flex-row gap-4`}>
          <div className="flex-1 relative">
            <i className={`ri-search-line absolute left-3 top-1/2 -translate-y-1/2 ${themeClasses.textSecondary}`}></i>
            <input
              type="text"
              placeholder={t("posProducts.searchPlaceholder")}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className={`w-full pl-10 pr-4 py-2 rounded-xl border ${themeClasses.border} ${themeClasses.inputBg} ${themeClasses.text} focus:outline-none focus:ring-2 focus:ring-primary-500`}
            />
          </div>

          <div className="flex gap-2">
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className={`px-4 py-2 rounded-xl border ${themeClasses.border} ${themeClasses.inputBg} ${themeClasses.text}`}
            >
              <option value="all">{t("posProducts.allStatuses")}</option>
              <option value="active">{t("posProducts.statusActive")}</option>
              <option value="inactive">{t("posProducts.statusInactive")}</option>
              <option value="out_of_stock">{t("posProducts.statusOutOfStock")}</option>
            </select>

            <div className={`flex rounded-xl border ${themeClasses.border} overflow-hidden`}>
              <button
                onClick={() => setViewMode("table")}
                className={`p-2 ${viewMode === "table" ? "bg-primary-500 text-gray-900" : themeClasses.text}`}
              >
                <i className="ri-list-check"></i>
              </button>
              <button
                onClick={() => setViewMode("grid")}
                className={`p-2 ${viewMode === "grid" ? "bg-primary-500 text-gray-900" : themeClasses.text}`}
              >
                <i className="ri-grid-line"></i>
              </button>
            </div>
          </div>
        </div>

        {viewMode === "table" ? (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className={`${themeClasses.cardBg} border-b ${themeClasses.border}`}>
                <tr>
                  <th className={`px-4 py-3 text-left text-sm font-medium ${themeClasses.textSecondary}`}>{t("posProducts.product")}</th>
                  <th className={`px-4 py-3 text-left text-sm font-medium ${themeClasses.textSecondary}`}>SKU</th>
                  <th className={`px-4 py-3 text-right text-sm font-medium ${themeClasses.textSecondary}`}>{t("posProducts.price")}</th>
                  <th className={`px-4 py-3 text-right text-sm font-medium ${themeClasses.textSecondary}`}>{t("posProducts.stock")}</th>
                  <th className={`px-4 py-3 text-center text-sm font-medium ${themeClasses.textSecondary}`}>{t("posProducts.status")}</th>
                  <th className={`px-4 py-3 text-center text-sm font-medium ${themeClasses.textSecondary}`}>{t("posProducts.visibility")}</th>
                  <th className={`px-4 py-3 text-right text-sm font-medium ${themeClasses.textSecondary}`}>{t("posProducts.actions")}</th>
                </tr>
              </thead>
              <tbody>
                {filteredProducts.map((product) => (
                  <tr key={product.id} className={`border-b ${themeClasses.border} ${themeClasses.tableRowHover}`}>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-gray-200 to-gray-300 dark:from-gray-600 dark:to-gray-700 flex items-center justify-center">
                          <i className={`ri-box-3-line ${themeClasses.textSecondary}`}></i>
                        </div>
                        <div>
                          <p className={`font-medium ${themeClasses.text}`}>{product.name}</p>
                          <p className={`text-sm ${themeClasses.textSecondary}`}>{product.category}</p>
                        </div>
                      </div>
                    </td>
                    <td className={`px-4 py-3 ${themeClasses.textSecondary}`}>{product.sku}</td>
                    <td className={`px-4 py-3 text-right font-medium ${themeClasses.text}`}>${product.price.toFixed(2)}</td>
                    <td className={`px-4 py-3 text-right ${product.stock < 20 ? "text-amber-500" : themeClasses.text}`}>
                      {product.stock}
                    </td>
                    <td className="px-4 py-3 text-center">{getStatusBadge(product.status)}</td>
                    <td className="px-4 py-3">
                      <div className="flex justify-center gap-2">
                        <span className={`px-2 py-1 rounded text-xs ${product.visibility.pos ? "bg-blue-500/20 text-blue-500" : "bg-gray-500/20 text-gray-500"}`}>
                          POS
                        </span>
                        <span className={`px-2 py-1 rounded text-xs ${product.visibility.online ? "bg-violet-500/20 text-violet-500" : "bg-gray-500/20 text-gray-500"}`}>
                          WEB
                        </span>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex justify-end gap-1">
                        <button aria-label={t("general.viewDetails", { ns: "common" })} className={`p-2 rounded-lg ${themeClasses.tableRowHover} ${themeClasses.text}`}>
                          <i className="ri-eye-line"></i>
                        </button>
                        <button aria-label={t("general.edit", { ns: "common" })} className={`p-2 rounded-lg ${themeClasses.tableRowHover} ${themeClasses.text}`}>
                          <i className="ri-edit-line"></i>
                        </button>
                        <button aria-label={t("general.delete", { ns: "common" })} className="p-2 rounded-lg hover:bg-red-500/10 text-red-500">
                          <i className="ri-delete-bin-line"></i>
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="p-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {filteredProducts.map((product) => (
              <div
                key={product.id}
                className={`${themeClasses.cardBg} rounded-xl p-4 border ${themeClasses.border}`}
              >
                <div className="aspect-square rounded-lg bg-gradient-to-br from-gray-200 to-gray-300 dark:from-gray-600 dark:to-gray-700 mb-3 flex items-center justify-center">
                  <i className={`ri-box-3-line text-4xl ${themeClasses.textSecondary}`}></i>
                </div>
                <div className="flex justify-between items-start mb-2">
                  <div>
                    <p className={`font-medium ${themeClasses.text}`}>{product.name}</p>
                    <p className={`text-sm ${themeClasses.textSecondary}`}>{product.sku}</p>
                  </div>
                  {getStatusBadge(product.status)}
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-primary-500 font-bold text-lg">${product.price.toFixed(2)}</span>
                  <span className={`text-sm ${themeClasses.textSecondary}`}>
                    {t("posProducts.stock")}: {product.stock}
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
